import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { api, formatApiError, formatCHF, formatDateTime } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Hourglass, CheckCircle, XCircle, X, Trash } from "@phosphor-icons/react";
import { getDisciplineCategories } from "../lib/disciplines";
import DeleteAccountDialog from "../components/DeleteAccountDialog";

export default function InstructorDashboard() {
  const { user, refresh } = useAuth();
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || "it";
  const DISCIPLINE_CATEGORIES = useMemo(() => getDisciplineCategories(lang), [lang]);

  const [courses, setCourses] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", discipline: "yoga", location: "", venue: "", address: "",
    starts_at: "", duration_min: 60, price_chf: 35, capacity: 12, image_url: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, b] = await Promise.all([api.get("/courses/mine"), api.get("/bookings/mine")]);
      setCourses(c.data);
      setBookings(b.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); refresh(); }, [load, refresh]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/courses", {
        ...form,
        starts_at: new Date(form.starts_at).toISOString(),
        duration_min: +form.duration_min,
        price_chf: +form.price_chf,
        capacity: +form.capacity,
      });
      toast.success(t("instructor_dash.course_submitted"));
      setOpen(false);
      setForm({ ...form, title: "", description: "", venue: "", starts_at: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const stats = {
    pending: courses.filter((c) => c.status === "pending").length,
    approved: courses.filter((c) => c.status === "approved").length,
    earnings: bookings
      .filter((b) => b.status === "confirmed" || b.status === "completed")
      .reduce((sum, b) => sum + (b.instructor_payout_chf || 0), 0),
    bookings: bookings.filter((b) => b.status === "confirmed" || b.status === "completed").length,
  };

  if (user?.instructor_status !== "approved") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="instructor-pending-page">
        <div className="border border-[#FFB800]/40 bg-[#0A0A0A] p-10 max-w-lg text-center">
          <Hourglass size={48} weight="duotone" className="text-[#FFB800] mx-auto" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800] font-bold mt-4">{t("instructor_dash.pending_tag")}</div>
          <h2 className="font-display text-3xl uppercase font-black mt-2">{t("instructor_dash.pending_title")}</h2>
          <p className="text-zinc-400 mt-3 text-sm">{t("instructor_dash.pending_message")}</p>
          <div className="mt-8 pt-6 border-t border-white/10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-3">
              {t("instructor_dash.danger_zone_label")}
            </div>
            <DeleteAccountDialog />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="instructor-dashboard">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* Payout-missing banner */}
        {!user?.payout_ready && (
          <Link to="/profile" data-testid="payout-banner" className="block mb-6 border border-[#FFB800]/50 bg-[#FFB800]/5 p-4 hover:bg-[#FFB800]/10 transition-colors">
            <div className="flex items-start gap-3">
              <div className="text-[#FFB800] font-black text-lg leading-none pt-0.5">!</div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-[#FFB800] font-bold">
                  {t("profile.payout_incomplete_tag")}
                </div>
                <div className="text-sm text-zinc-300 mt-1">
                  {t("profile.payout_incomplete_body")} <span className="text-[#FFB800] underline">{t("profile.link_label")} →</span>
                </div>
              </div>
            </div>
          </Link>
        )}

        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("instructor_dash.tag")}</div>
            <h1 className="font-display text-5xl uppercase font-black mt-2 tracking-tight">{t("instructor_dash.title")}</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none h-11 btn-tactical" data-testid="new-course-button">
                <Plus size={16} className="mr-2" />{t("instructor_dash.new_course")}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/15 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-course-dialog">
              <DialogHeader>
                <DialogTitle className="font-display uppercase text-2xl">{t("instructor_dash.new_course")}</DialogTitle>
                <DialogDescription className="text-zinc-400 text-xs uppercase tracking-wider">
                  {t("instructor_dash.new_course_dialog_desc")}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4 mt-2">
                <FormField label={t("instructor_dash.field_title")} required>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required
                    className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="course-form-title" />
                </FormField>
                <FormField label={t("instructor_dash.field_description")} required>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required
                    className="bg-black border-white/20 rounded-none focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="course-form-description" />
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label={t("instructor_dash.field_discipline")} required>
                    <Select value={form.discipline} onValueChange={(v) => setForm({ ...form, discipline: v })}>
                      <SelectTrigger className="bg-black border-white/20 rounded-none h-11" data-testid="course-form-discipline"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/20 rounded-none max-h-[50vh]">
                        {DISCIPLINE_CATEGORIES.map(([cat, items]) => (
                          <React.Fragment key={cat}>
                            <div className="px-2 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-[#E30613] font-bold">{cat}</div>
                            {items.map((d) => <SelectItem key={d.id} value={d.id}>{d.displayLabel}</SelectItem>)}
                          </React.Fragment>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField label={t("instructor_dash.field_starts_at")} required>
                    <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} required
                      className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="course-form-starts-at" />
                  </FormField>
                  <FormField label={t("instructor_dash.field_city")} required>
                    <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required placeholder={t("instructor_dash.field_city_placeholder")}
                      className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="course-form-location" />
                  </FormField>
                  <FormField label={t("instructor_dash.field_venue")} required>
                    <Input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} required placeholder={t("instructor_dash.field_venue_placeholder")}
                      className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="course-form-venue" />
                  </FormField>
                </div>
                <FormField label={t("instructor_dash.field_address")}>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="course-form-address" />
                </FormField>
                <div className="grid grid-cols-3 gap-3">
                  <FormField label={t("instructor_dash.field_duration")} required>
                    <Input type="number" min={15} value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
                      className="bg-black border-white/20 rounded-none h-11" data-testid="course-form-duration" />
                  </FormField>
                  <FormField label={t("instructor_dash.field_price")} required>
                    <Input type="number" min={0} step="0.50" value={form.price_chf} onChange={(e) => setForm({ ...form, price_chf: e.target.value })}
                      className="bg-black border-white/20 rounded-none h-11" data-testid="course-form-price" />
                  </FormField>
                  <FormField label={t("instructor_dash.field_capacity")} required>
                    <Input type="number" min={1} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                      className="bg-black border-white/20 rounded-none h-11" data-testid="course-form-capacity" />
                  </FormField>
                </div>
                <FormField label={t("instructor_dash.field_image")}>
                  <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..."
                    className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="course-form-image" />
                </FormField>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider border-t border-white/10 pt-3">
                  {t("instructor_dash.commission_note", { amount: form.price_chf ? formatCHF(form.price_chf * 0.9) : "—" })}
                </div>
                <Button type="submit" disabled={saving}
                  className="w-full bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none h-12 btn-tactical"
                  data-testid="course-form-submit">{saving ? t("instructor_dash.submitting") : t("instructor_dash.submit")}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10" data-testid="instructor-stats">
          <StatBox label={t("instructor_dash.stat_pending")} value={stats.pending} color="text-[#FFB800]" />
          <StatBox label={t("instructor_dash.stat_published")} value={stats.approved} color="text-[#00FF66]" />
          <StatBox label={t("instructor_dash.stat_bookings")} value={stats.bookings} color="text-white" />
          <StatBox label={t("instructor_dash.stat_earnings")} value={formatCHF(stats.earnings)} color="text-[#E30613]" />
        </div>

        <EarningsPanel t={t} />

        <Tabs defaultValue="courses" className="mt-10">
          <TabsList className="bg-transparent border-b border-white/10 w-full justify-start rounded-none h-auto p-0 gap-6">
            <TabsTrigger value="courses" className="data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-[#E30613] rounded-none uppercase tracking-widest text-xs font-bold pb-3 px-1" data-testid="tab-courses">
              {t("instructor_dash.tab_courses")}
            </TabsTrigger>
            <TabsTrigger value="bookings" className="data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-[#E30613] rounded-none uppercase tracking-widest text-xs font-bold pb-3 px-1" data-testid="tab-bookings">
              {t("instructor_dash.tab_bookings")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="courses" className="mt-6">
            {courses.length === 0 ? (
              <div className="border border-white/10 p-12 text-center bg-[#0A0A0A]">
                <div className="text-zinc-400">{t("instructor_dash.no_courses")}</div>
              </div>
            ) : (
              <div className="space-y-3" data-testid="instructor-courses-list">
                {courses.map((c) => (
                  <div key={c.id} className="border border-white/10 bg-[#0A0A0A] p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`instructor-course-${c.id}`}>
                    <div>
                      <div className="font-display text-xl uppercase font-black">{c.title}</div>
                      <div className="text-xs text-zinc-400 uppercase tracking-wider mt-1">
                        {formatDateTime(c.starts_at)} · {c.location} · {c.venue}
                      </div>
                      {c.rejection_reason && <div className="text-xs text-[#E30613] mt-1">{t("instructor_dash.rejection_reason", { reason: c.rejection_reason })}</div>}
                      {c.cancellation_reason && <div className="text-xs text-zinc-500 mt-1">{c.cancellation_reason}</div>}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="font-display text-xl font-black">{formatCHF(c.price_chf)}</div>
                        <div className="text-[10px] text-zinc-500 uppercase">{t("instructor_dash.seats_progress", { booked: c.bookings_count, total: c.capacity })}</div>
                      </div>
                      <StatusBadge status={c.status} t={t} />
                      {c.status === "approved" && new Date(c.starts_at) > new Date() && (
                        <CancelCourseButton course={c} onCancelled={load} t={t} />
                      )}
                      {c.status === "pending" && (
                        <DeleteCourseButton course={c} onDeleted={load} t={t} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            {bookings.length === 0 ? (
              <div className="border border-white/10 p-12 text-center bg-[#0A0A0A]">
                <div className="text-zinc-400">{t("instructor_dash.no_bookings")}</div>
              </div>
            ) : (
              <div className="space-y-3" data-testid="instructor-bookings-list">
                {bookings.map((b) => (
                  <div key={b.id} className="border border-white/10 bg-[#0A0A0A] p-5 flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="font-display text-lg uppercase font-black">{b.course_title}</div>
                      <div className="text-xs text-zinc-400 uppercase tracking-wider mt-1">
                        {t("instructor_dash.client")}: {b.client_name} · {formatDateTime(b.course_starts_at)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-lg font-black text-[#00FF66]">{formatCHF(b.instructor_payout_chf)}</div>
                      <div className="text-[10px] text-zinc-500 uppercase">{b.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">{label}{required && <span className="text-[#E30613]"> *</span>}</Label>
      {children}
    </div>
  );
}

function StatBox({ label, value, color = "text-white" }) {
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-5">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{label}</div>
      <div className={`font-display text-3xl font-black mt-2 ${color}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ status, t }) {
  const map = {
    pending: { c: "bg-[#FFB800] text-black", l: t("instructor_dash.status_pending"), I: Hourglass },
    approved: { c: "bg-[#00FF66] text-black", l: t("instructor_dash.status_published"), I: CheckCircle },
    rejected: { c: "bg-[#E30613] text-white", l: t("instructor_dash.status_rejected"), I: XCircle },
    cancelled: { c: "bg-zinc-700 text-zinc-300", l: t("my_bookings.status_cancelled"), I: X },
  };
  const v = map[status] || { c: "bg-zinc-700 text-white", l: status, I: Hourglass };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-bold ${v.c}`}>
      <v.I size={10} weight="fill" />{v.l}
    </span>
  );
}

function CancelCourseButton({ course, onCancelled, t }) {
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/courses/${course.id}/cancel`, { reason });
      toast.success(t("instructor_dash.cancel_success", { count: data.affected_bookings }));
      setOpen(false);
      onCancelled?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm"
          className="border-[#E30613] text-[#E30613] hover:bg-[#E30613]/10 uppercase tracking-widest text-[10px] font-bold rounded-none h-9"
          data-testid={`cancel-course-${course.id}`}>
          <X size={12} className="mr-1" weight="bold" />{t("instructor_dash.cancel_course")}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border-white/15 rounded-none max-w-md" data-testid="cancel-course-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase text-2xl">{t("instructor_dash.cancel_course_dialog_title")}</DialogTitle>
          <DialogDescription className="text-zinc-400">{t("instructor_dash.cancel_course_dialog_desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="border border-white/10 p-3 text-xs">
            <div className="font-bold text-white">{course.title}</div>
            <div className="text-zinc-400 mt-1">{formatDateTime(course.starts_at)} · {course.bookings_count} {t("instructor_dash.client").toLowerCase()}</div>
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">{t("instructor_dash.cancel_reason")}</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder={t("instructor_dash.cancel_reason_placeholder")}
              className="bg-black border-white/20 rounded-none focus-visible:ring-[#E30613] focus-visible:border-[#E30613]"
              data-testid="cancel-reason-input" />
          </div>
          <Button onClick={submit} disabled={busy}
            className="w-full bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none h-12"
            data-testid="cancel-confirm-button">
            {busy ? "..." : t("instructor_dash.cancel_confirm")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteCourseButton({ course, onDeleted, t }) {  const [busy, setBusy] = React.useState(false);
  const del = async () => {
    if (!window.confirm(t("instructor_dash.confirm_delete_course", { title: course.title }))) return;
    setBusy(true);
    try {
      await api.delete(`/courses/${course.id}`);
      toast.success(t("instructor_dash.course_deleted"));
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant="outline" size="sm" disabled={busy} onClick={del}
      className="border-zinc-700 text-zinc-400 hover:bg-red-900/20 hover:border-red-500 hover:text-red-400 uppercase tracking-widest text-[10px] font-bold rounded-none h-9"
      data-testid={`delete-course-${course.id}`}>
      <Trash size={12} className="mr-1" weight="bold" />{t("instructor_dash.delete_course")}
    </Button>
  );
}

function EarningsPanel({ t }) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    api.get("/instructors/me/earnings")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);
  if (!data) return null;
  return (
    <div className="mt-6 border border-white/10 bg-[#0A0A0A] p-5 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="earnings-panel">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{t("instructor_dash.earnings_pending")}</div>
        <div className="font-display text-2xl font-black text-[#FFB800] mt-1" data-testid="earnings-pending">
          {formatCHF(data.total_pending_chf)}
        </div>
        <div className="text-[10px] text-zinc-500 mt-1">{data.pending_bookings_count} {t("admin_dash.bookings")}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{t("instructor_dash.earnings_paid")}</div>
        <div className="font-display text-2xl font-black text-[#00FF66] mt-1" data-testid="earnings-paid">
          {formatCHF(data.total_paid_chf)}
        </div>
        <div className="text-[10px] text-zinc-500 mt-1">{data.paid_bookings_count} {t("admin_dash.bookings")}</div>
      </div>
      <div className="md:border-l md:border-white/10 md:pl-4">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{t("instructor_dash.earnings_note_title")}</div>
        <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
          {t("instructor_dash.earnings_note_body")}
        </div>
      </div>
    </div>
  );
}
